import { Fragment, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'

import CreateScenarioFromAttack from './CreateScenarioFromAttack'

import ServiceBasedAttackHeader from 'components/attacks/details/ServiceBasedAttackHeader'
import { hasTruthy } from 'utils/list'
import useAuth from 'hooks/use-auth'
import OnboardingAttackModal from 'components/attacks/details/OnboardingAttackModal'
import useMetadata from 'hooks/use-metadata'
import useAgent from 'hooks/use-agent'
import { isAttackInCompleteState } from 'components/attacks/utils/attack-stages'
import Section from 'components/common/Section'
import ContentErrorMessage from 'components/common/ContentErrorMessage'
import AttackMetricsChartTeaser from 'components/attacks/details/AttackMetricsChartTeaser'
import DetailPanel from 'components/attacks/details/DetailPanel'
import TargetingPanel from 'components/attacks/details/TargetingPanel'
import ExecutionPanel from 'components/attacks/details/ExecutionPanel'
import InfraAttackResultsSection from 'components/attacks/details/InfraAttackResultsSection'
import AdhocAttackMetrics from 'components/attacks/details/AdhocAttackMetrics'
import { generateRerunAttackLinkTo } from 'components/attacks/utils/rerun'
import {
  convertAttackToScenarioState,
  copyAttackForReuse
} from 'components/attacks/utils/attacks'
import { attackSupportsMetrics } from 'utils/attack-metrics'
import useAttackMetricsStatus from 'hooks/use-attack-metrics-status'
import theme from 'theme'

export function DataVisualizationChartDetail({
  attack,
  isScenario,
  attackMetricsGraphProps
}) {
  const {
    isOptIn,
    canUpdateCompanyAttackVisualizations
  } = useAttackMetricsStatus()

  // Prevent loading the chart if we know there's no possibility of metrics for this attack type
  if (
    !attackSupportsMetrics(
      attack?.infra_command?.commandType,
      attack?.target_type
    )
  ) {
    return null
  }

  if (isOptIn) {
    return (
      <AdhocAttackMetrics
        attack={attack}
        darkMode={isScenario}
        {...attackMetricsGraphProps}
      />
    )
  } else if (canUpdateCompanyAttackVisualizations) {
    return <AttackMetricsChartTeaser darkMode={isScenario} />
  } else {
    return null
  }
}

DataVisualizationChartDetail.propTypes = {
  isScenario: PropTypes.bool,
  attack: PropTypes.object,
  attackMetricsGraphProps: PropTypes.object
}

export default function InfraAttackContents({
  attackId,
  referenceAttack,
  isScenario,
  displayFirstAttackModal = false,
  attackMetricsGraphProps
}) {
  const { currentTeamId } = useAuth()
  const history = useHistory()
  const [attackShouldPoll, setAttackShouldPoll] = useState(
    !isAttackInCompleteState(referenceAttack)
  )
  const [isFirstAttackModalShown, setIsFirstAttackModalShown] = useState(false)
  const { metadata } = useMetadata()

  const {
    data: [attack, executions],
    isLoading,
    error: [attackError],
    doFetch
  } = useAgent(
    [
      `/attacks/${attackId}?teamId=${currentTeamId}`,
      `executions?taskId=${attackId}&teamId=${currentTeamId}`
    ],
    [referenceAttack, []],
    {
      shouldCache: true,
      interval: attackShouldPoll ? 4000 : false
    }
  )

  const isServiceBasedAttack = typeof attack?.service_id === 'string'

  const { data: service, isLoading: serviceLoading } = useAgent(
    `/prototype/services/${attack?.service_id}?teamId=${currentTeamId}`,
    [],
    {
      shouldCache: true,
      skipRequest: !isServiceBasedAttack
    }
  )

  // Stop polling for the attack data if its in completed stage
  useEffect(() => {
    if (attackShouldPoll && attack && isAttackInCompleteState(attack)) {
      setAttackShouldPoll(false)
      if (attack.create_source_detail === 'Onboarding') {
        setIsFirstAttackModalShown(true)
      }
    }
  }, [attack, attackShouldPoll])

  const handleSubmit = (name, description, hypothesis) => {
    // we specifically want to try and use `attack` first here
    // because it will have the dynamically_resolved_ips, which are needed in some cases
    // `executions` does not have this data
    const reusedAttack = copyAttackForReuse(attack ?? executions[0], metadata)

    history.push({
      pathname: '/scenarios/new/hosts',
      state: {
        ...convertAttackToScenarioState(
          reusedAttack,
          name,
          description,
          hypothesis
        )
      }
    })
  }

  const rerunAttackLink = generateRerunAttackLinkTo(attack, metadata)

  return (
    <Section
      data-test-attack-contents
      css={{
        display: 'grid',
        gap: theme.space[4],
        gridAutoRows: 'max-content'
      }}
      isLoading={hasTruthy(isLoading, isServiceBasedAttack && serviceLoading)}
    >
      {attackError ? (
        <ContentErrorMessage
          destination="/attacks"
          fetchError={attackError}
          returnText="Return to Attacks"
        />
      ) : (
        <Fragment>
          {isServiceBasedAttack && (
            <ServiceBasedAttackHeader
              rerunAttackLink={rerunAttackLink}
              service={service}
            />
          )}
          <DataVisualizationChartDetail
            attack={attack}
            attackMetricsGraphProps={attackMetricsGraphProps}
            isScenario={isScenario}
          />
          {!isServiceBasedAttack && !isScenario && (
            <CreateScenarioFromAttack
              rerunLink={rerunAttackLink}
              onSubmit={handleSubmit}
            />
          )}
          <Section
            css={{
              display: 'grid',
              gap: theme.space[4]
            }}
          >
            <DetailPanel
              attack={attack}
              doFetch={doFetch}
              isScenario={isScenario}
              metadata={metadata}
              rerunLinkTo={rerunAttackLink}
              showRerunButton={false}
            />
            <TargetingPanel attack={attack} />
            <InfraAttackResultsSection attack={attack} doFetch={doFetch} />
            <ExecutionPanel attackId={attack?.guid} executions={executions} />
          </Section>
        </Fragment>
      )}
      <OnboardingAttackModal
        open={displayFirstAttackModal && isFirstAttackModalShown}
        onCancel={() => {
          setIsFirstAttackModalShown(false)
        }}
      />
    </Section>
  )
}

InfraAttackContents.propTypes = {
  attackId: PropTypes.string,
  referenceAttack: PropTypes.object,
  displayFirstAttackModal: PropTypes.bool,
  isScenario: PropTypes.bool,
  attackMetricsGraphProps: PropTypes.object
}

InfraAttackContents.defaultProps = {
  isScenario: false,
  isLoading: false,
  executions: []
}
